import React from 'react';

import { standardRequestWithFlash } from 'shared/utils/request';
import styled, { useTheme } from 'styled-components';
import {
  Card,
  CardBody,
  CardHeader,
  // @ts-expect-error - TS7016 - Could not find a declaration file for module '@grnhse/seedling/lib/azalea/components/card'. 'node_modules/@grnhse/seedling/lib/azalea/components/card.js' implicitly has an 'any' type.
} from '@grnhse/seedling/lib/azalea/components/card';
import { getCardGutter } from 'gdpr/components/shared/card';
import {
  BodyText,
  BoldBody,
  SecondaryBodyText,
} from '@grnhse/seedling/lib/azalea/typography';
import { colors, fontWeight } from '@grnhse/seedling/lib/azalea/constants';
import Button from '@grnhse/seedling/lib/azalea/components/button';
import RadioGroup from '@grnhse/seedling/lib/azalea/components/radio_group';
import Radio from '@grnhse/seedling/lib/azalea/components/radio';
import ConfirmModal from '@grnhse/seedling/lib/azalea/components/modals/confirm_modal';
import ShowDetails from './show_details';
import t from 'shared/utils/translation';
import { FooterDivider } from './shared/divider';
import type { LegalBasisType } from './shared/types';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import ConfirmationModal from '@grnhse/seedling/lib/birch/components/modals/confirmation_modal';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import BirchRadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import { PrimaryButton, SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Grid } from '@grnhse/seedling/lib/azalea/layouts/grid';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Body } from '@grnhse/seedling/lib/birch/typography';

type Props = {
  legalBasis: LegalBasisType;
  singlePurposeConsentEnabled: boolean;
  processingLegalBasis: LegalBasisType;
  retentionLegalBasis: LegalBasisType;
  emailTemplatePath: string;
};

export default function LegalBasis(props: Props) {
  const [editing, setEditing] = React.useState(false);
  const [editModalIsOpen, openEditModal, closeEditModal] = useModal();
  const [exitModalIsOpen, openExitModal, closeExitModal] = useModal();
  const [legalBasis, setLegalBasis] = React.useState(props.legalBasis);
  const [processingLegalBasis, setProcessingLegalBasis] = React.useState(props.processingLegalBasis);
  const [retentionLegalBasis, setRetentionLegalBasis] = React.useState(props.retentionLegalBasis);
  const showConfirmModal = legalBasis !== props.legalBasis;
  const [submitting, setSubmitting] = React.useState(false);

  function saveLegalBasis() {
    setEditing(false);
    setSubmitting(true);

    let payload;
    if (props.singlePurposeConsentEnabled) {
      payload = { processing_legal_basis: processingLegalBasis, retention_legal_basis: retentionLegalBasis };
    }
    else {
      payload = { legal_basis: legalBasis };
    }

    standardRequestWithFlash
      .put('/gdpr/legal_basis', payload)
      .then(() => {
        location.reload();
      });
  }

  function CancelLink() {
    const theme = useTheme();
    // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'DefaultTheme'.
    const isBirch = theme?.name === 'birch';

    if (showConfirmModal) {
      return (
        <ConfirmModal
          title={t('gdpr.shared.confirm_cancel')}
          confirmText={t('gdpr.shared.lose_unsaved_changes')}
          cancelText={t('gdpr.shared.continue_editing')}
          submitText={t('gdpr.shared.exit_without_saving')}
          maxWidth="sm"
          onConfirm={() => {
            setEditing(false);
            setLegalBasis(props.legalBasis);
          }}
        >
          <CancelButton hideBg redesign={isBirch}>
            {t('gdpr.shared.cancel')}
          </CancelButton>
        </ConfirmModal>
      );
    } else {
      return (
        <CancelButton
          hideBg
          onClick={() => setEditing(false)}
          redesign={isBirch}
        >
          {t('gdpr.shared.cancel')}
        </CancelButton>
      );
    }
  }

  function SaveButton() {
    if (showConfirmModal) {
      return (
        <ConfirmModal
          title={t('gdpr.legal_basis.changing_legal_basis')}
          confirmText={
            legalBasis === 'explicit_consent'
              ? t('gdpr.legal_basis.changing_to_explicit_consent')
              : t('gdpr.legal_basis.changing_to_legitimate_interest')
          }
          cancelText={t('gdpr.shared.continue_editing')}
          submitText={t('gdpr.legal_basis.change_legal_basis')}
          maxWidth="sm"
          onConfirm={saveLegalBasis}
        >
          <StyledSaveButton fancy>{t('gdpr.shared.save')}</StyledSaveButton>
        </ConfirmModal>
      );
    } else {
      return (
        <StyledSaveButton fancy onClick={() => setEditing(false)}>
          {t('gdpr.shared.save')}
        </StyledSaveButton>
      );
    }
  }

  function EditMode() {
    return (
      <React.Fragment>
        <CardBody
          cardGutter={getCardGutter(false)}
          verticalCardGutter={'medium'}
        >
          <div>
            <SecondaryBodyText>
              {t('gdpr.legal_basis.define_your_legal_basis')}
            </SecondaryBodyText>
            <RadioSection>
              <RadioGroup
                className="contact-preference"
                name="communalEmail"
                value={legalBasis}
                onChange={(event: React.SyntheticEvent<HTMLInputElement>) => {
                  setLegalBasis(event.currentTarget.value as LegalBasisType);
                }}
              >
                <Radio
                  value="legitimate_interest"
                  // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
                  label={
                    // $FlowFixMe
                    <RadioLabel>
                      <strong>
                        {t('gdpr.legal_basis.legitimate_interest')}
                      </strong>
                      <p>{t('gdpr.legal_basis.legitimate_interest_info')}</p>
                    </RadioLabel>
                  }
                />
                {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                <ShowDetailsSection
                  showComponent={
                    <ShowContainer>
                      <p>
                        {t(
                          'gdpr.legal_basis.legitimate_interest_info_expanded.p1'
                        )}
                      </p>
                      <p>
                        {t(
                          'gdpr.legal_basis.legitimate_interest_info_expanded.p2'
                        )}
                      </p>
                    </ShowContainer>
                  }
                />
                <Radio
                  value="explicit_consent"
                  // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
                  label={
                    // $FlowFixMe
                    <RadioLabel>
                      <strong>{t('gdpr.legal_basis.explicit_consent')}</strong>
                      <p>{t('gdpr.legal_basis.explicit_consent_info')}</p>
                    </RadioLabel>
                  }
                />

                {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                <ShowDetailsSection
                  showComponent={
                    <ShowContainer>
                      <p>
                        {t(
                          'gdpr.legal_basis.explicit_consent_info_expanded.p1'
                        )}
                      </p>
                      <p>
                        {t(
                          'gdpr.legal_basis.explicit_consent_info_expanded.p2'
                        )}
                      </p>
                      <p>
                        {t(
                          'gdpr.legal_basis.explicit_consent_info_expanded.p3'
                        )}{' '}
                        <a href={props.emailTemplatePath}>
                          View Email Template
                        </a>
                      </p>
                    </ShowContainer>
                  }
                />
              </RadioGroup>
            </RadioSection>
          </div>
          <FooterDivider />
          <SubmitButtons>
            <CancelLink />
            <SaveButton />
          </SubmitButtons>
        </CardBody>
      </React.Fragment>
    );
  }

  function ReadMode() {
    function SingleBasis() {
      return (
        <React.Fragment>
          <BodyText>
            <BoldBody>{t(`gdpr.legal_basis.${legalBasis}`)}</BoldBody>
          </BodyText>
          <BodyText>{t(`gdpr.legal_basis.${legalBasis}_info`)}</BodyText>
        </React.Fragment>
      );
    }

    function SplitBasis() {
      return (
        <React.Fragment>
          <BodyText>
            <BoldBody>{t('gdpr.set_legal_basis.card.consent_to_process')}:&nbsp;</BoldBody>
            {t(`gdpr.set_legal_basis.${props.processingLegalBasis}`)}
          </BodyText>
          <BodyText>
            <BoldBody>{t('gdpr.set_legal_basis.card.consent_to_retain')}:&nbsp;</BoldBody>
            {t(`gdpr.set_legal_basis.${props.retentionLegalBasis}`)}
          </BodyText>
        </React.Fragment>
      );
    }

    return (
      <CardBody verticalCardGutter="medium">
        {props.singlePurposeConsentEnabled ? <SplitBasis /> : <SingleBasis />}
      </CardBody>
    );
  }

  function EditModal() {
    const cancel = () => {
      const processingLegalBasisChanged = processingLegalBasis !== props.processingLegalBasis;
      const retentionLegalBasisChanged = retentionLegalBasis !== props.retentionLegalBasis;

      if (processingLegalBasisChanged || retentionLegalBasisChanged) {
        openExitModal();
      }
      
      closeEditModal();
    };

    return (
      <DataEntryModal
        size="large"
        isOpen={editModalIsOpen}
        onDismiss={cancel}
        onSubmit={saveLegalBasis}
        data-provides="edit-modal"
        ariaLabel={t('gdpr.set_legal_basis.edit_modal.header')}
      >
        <DataEntryModal.Header onClick={cancel}>
          {t('gdpr.set_legal_basis.edit_modal.header')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <Stack spacing={3} sx={{ paddingBottom: 1.5 }}>
            <InformationalCallOut
              headerText={t('gdpr.set_legal_basis.edit_modal.callout.title')}
              bodyText={t('gdpr.set_legal_basis.edit_modal.callout.body')}
              linkProps={{
                children: t('gdpr.set_legal_basis.edit_modal.callout.learn_more'),
                href: 'https://support.greenhouse.io/hc/en-us/articles/14742758333595-Single-purpose-consent-overview',
                target: '_blank'
              }}
            />
            <Stack spacing={0}>
              <RadioHeader medium>
                {t('gdpr.set_legal_basis.edit_modal.processing.title')}
              </RadioHeader>
              <RadioSubheader>
                {t('gdpr.set_legal_basis.edit_modal.processing.label')}
              </RadioSubheader>
              <BirchRadioGroup
                data-provides="processing-legal-basis"
                value={processingLegalBasis}
                onChange={(value: string | number, e?: React.SyntheticEvent<HTMLInputElement, Event> | undefined) => setProcessingLegalBasis(value as LegalBasisType)}
                ariaLabel={t('gdpr.set_legal_basis.edit_modal.processing.label')}
                options={[
                  {
                    label: t('gdpr.set_legal_basis.legitimate_interest'),
                    value: 'legitimate_interest'
                  },
                  {
                    label: t('gdpr.set_legal_basis.explicit_consent'),
                    value: 'explicit_consent'
                  }
                ]}
              />
            </Stack>
            <Stack spacing={0}>
              <RadioHeader medium>
                {t('gdpr.set_legal_basis.edit_modal.retention.title')}
              </RadioHeader>
              <RadioSubheader>
                {t('gdpr.set_legal_basis.edit_modal.retention.label')}
              </RadioSubheader>
              <BirchRadioGroup
                data-provides="retention-legal-basis"
                value={retentionLegalBasis}
                onChange={(value: string | number, e?: React.SyntheticEvent<HTMLInputElement, Event> | undefined) => setRetentionLegalBasis(value as LegalBasisType)}
                ariaLabel={t('gdpr.set_legal_basis.edit_modal.retention.label')}
                options={[
                  {
                    label: t('gdpr.set_legal_basis.legitimate_interest'),
                    value: 'legitimate_interest'
                  },
                  {
                    label: t('gdpr.set_legal_basis.explicit_consent'),
                    value: 'explicit_consent'
                  }
                ]}
              />
            </Stack>
          </Stack>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Grid item>
            <SecondaryButton onClick={cancel}>{t('gdpr.set_legal_basis.edit_modal.cta.cancel')}</SecondaryButton>
          </Grid>
          <Grid item>
            <PrimaryButton type="submit" disabled={submitting}>
              {t('gdpr.set_legal_basis.edit_modal.cta.save_changes')}
            </PrimaryButton>
          </Grid>
        </DataEntryModal.Footer>
      </DataEntryModal>
    );
  }

  function ExitModal() {
    const continueEditing = () => {
      closeExitModal();
      openEditModal();
    };

    const leaveWithoutSaving = () => {
      closeExitModal();

      setProcessingLegalBasis(props.processingLegalBasis);
      setRetentionLegalBasis(props.retentionLegalBasis);
    };

    return (
      <ConfirmationModal
        isOpen={exitModalIsOpen}
        ariaLabel={t('gdpr.set_legal_basis.exit_modal.header')}
      >
        <ConfirmationModal.Header>
          {t('gdpr.set_legal_basis.exit_modal.header')}
        </ConfirmationModal.Header>
        <ConfirmationModal.Body>
          <BodyText>
            {t('gdpr.set_legal_basis.exit_modal.body')}
          </BodyText>
        </ConfirmationModal.Body>
        <ConfirmationModal.Footer>
          <Grid item>
            <SecondaryButton onClick={continueEditing}>
              {t('gdpr.set_legal_basis.exit_modal.cta.continue_editing')}
            </SecondaryButton>
          </Grid>
          <Grid item>
            <PrimaryButton onClick={leaveWithoutSaving}>
              {t('gdpr.set_legal_basis.exit_modal.cta.leave')}
            </PrimaryButton>
          </Grid>
        </ConfirmationModal.Footer>
      </ConfirmationModal>
    );
  };

  return (
    <Card useLegacyPadding data-test-legal-basis-section>
      <CardHeader
        label={t('gdpr.legal_basis.title')}
        labelSize="large"
        rightComponent={
          editing ? null : (
            <Button
              onClick={() => {
                if (props.singlePurposeConsentEnabled) {
                  openEditModal();
                }
                else {
                  setEditing(true);
                }
              }}
            >
              {t('gdpr.shared.configure')}
            </Button>
          )
        }
      />
      {editing ? <EditMode /> : <ReadMode />}
      <EditModal />
      <ExitModal />
    </Card>
  );
}

const RadioLabel = styled.div`
  padding-top: 17px;
  line-height: 20px;
  strong {
    font-size: 14px;
    ${fontWeight('heavy')};
    color: ${colors.darkestGrey};
  }
  p {
    font-size: 14px;
    ${fontWeight('normal')};
    color: ${colors.darkestGrey};
  }
`;

// box-sizing unset fixes a styling issue for radio buttons. Without this, the
// selected radio circle state looks off centered.
const RadioSection = styled.div`
  margin-left: 20px;
  *,
  *::after,
  *::before {
    box-sizing: unset;
  }
`;

const RadioHeader = styled(Body)`
  margin: 0 0 4px 0;
`;

const RadioSubheader = styled(Body)`
  margin: 0 0 8px 0;
`;

const StyledSaveButton = styled(Button)`
  display: inline-block;
`;

const SubmitButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ShowDetailsSection = styled(ShowDetails)`
  margin-left: 27px;
`;

const ShowContainer = styled.div`
  color: ${colors.darkGrey};
  width: 458px;

  p,
  a {
    font-size: 12px;
    margin-top: 10px;
    ${fontWeight('normal')};
  }
  p:last-child {
    margin-bottom: 10px;
  }
`;

const CancelButton = styled(Button)`
  ${(props) => props.redesign && `text-decoration: underline;`};
`;
